import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getAcademicContent,
  getContentFilters,
  isContentSuccess,
  formatFileSize,
  AcademicContentItem,
  ContentFilterOptions
} from '../utils/academicContentApiClient';
import '../styles/AcademicContent.css';

const PAGE_SIZE = 12;

/**
 * Content types get a colour so a student scanning for mind maps finds them
 * without reading every card. Keyed by name rather than id because the ids are
 * seeded and could differ between environments.
 */
const TYPE_CLASS: Record<string, string> = {
  'Detailed Notes': 'type-detailed',
  'Revision Notes': 'type-revision',
  'Mind Maps': 'type-mindmap',
  'Flash Cards': 'type-flashcard',
  'Concept Videos': 'type-video'
};

const AcademicContent: React.FC = () => {
  // Filters live in the URL so a student can bookmark "NEET / 12 / Physics"
  // and share it. It also means the browser back button steps back through
  // filter changes, which is what people expect from a catalogue.
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<AcademicContentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<ContentFilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const examId = searchParams.get('exam_id') || '';
  const classGroupId = searchParams.get('class_group_id') || '';
  const subjectId = searchParams.get('subject_id') || '';
  const contentTypeId = searchParams.get('content_type_id') || '';
  const chapterId = searchParams.get('chapter_id') || '';

  const hasFilters = !!(examId || classGroupId || subjectId || contentTypeId || chapterId);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    // Changing the subject invalidates the chapter: chapters belong to one
    // subject, so keeping the old one would filter to an empty list.
    if (key === 'subject_id') {
      next.delete('chapter_id');
    }
    // Any filter change returns to page one. Staying on page 4 of a narrower
    // result set shows an empty grid that looks like a failure.
    next.delete('page');
    setSearchParams(next);
  };

  const clearFilters = () => setSearchParams(new URLSearchParams());

  const goToPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Lookups are fetched once and then held. They change only when an admin
  // adds a subject, which is not something a student session needs to notice.
  useEffect(() => {
    let cancelled = false;
    getContentFilters()
      .then((response: any) => {
        if (cancelled) return;
        if (isContentSuccess(response)) {
          setFilters(response.result);
        }
      })
      .catch(() => {
        // A failed lookup fetch leaves the filter bar empty but must not stop
        // the library itself from rendering.
        if (!cancelled) setFilters(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadContent = useCallback(() => {
    setLoading(true);
    setError(null);

    getAcademicContent({
      exam_id: examId,
      class_group_id: classGroupId,
      subject_id: subjectId,
      content_type_id: contentTypeId,
      chapter_id: chapterId,
      page,
      pagesize: PAGE_SIZE
    })
      .then((response: any) => {
        if (isContentSuccess(response)) {
          setItems(response.result || []);
          setTotal(response.total || 0);
        } else {
          setItems([]);
          setTotal(0);
        }
      })
      .catch(() => {
        setError('We could not load the content library. Please check your connection and try again.');
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [examId, classGroupId, subjectId, contentTypeId, chapterId, page]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Chapters are filtered in the browser. The API returns them all with their
  // subject_id so picking a subject does not cost a round trip.
  const chaptersForSubject = (filters?.chapter || []).filter(
    (c: any) => !subjectId || String(c.subject_id) === String(subjectId)
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openItem = (item: AcademicContentItem) => {
    const url = item.media === 'video' ? item.video_url : item.file_url;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="academic-content-page min-vh-100">
      <div className="container py-5">

        <div className="row mb-4">
          <div className="col-12">
            <h2 className="fw-bold mb-2">
              <i className="fas fa-book-open me-3 text-primary"></i>
              Academic Content
            </h2>
            <p className="text-muted mb-0">
              Notes, mind maps and flash cards for NEET and JEE, free for every WiZi student.
            </p>
          </div>
        </div>

        {/* Filter bar */}
        <div className="card border-0 shadow-sm mb-4 filter-card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-exam">Exam</label>
                <select
                  id="filter-exam"
                  className="form-select"
                  value={examId}
                  onChange={(e) => setFilter('exam_id', e.target.value)}
                >
                  <option value="">All exams</option>
                  {(filters?.exam || []).map((o: any) => (
                    <option key={o.id} value={o.id}>{o.exam_name}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-class">Class</label>
                <select
                  id="filter-class"
                  className="form-select"
                  value={classGroupId}
                  onChange={(e) => setFilter('class_group_id', e.target.value)}
                >
                  <option value="">All classes</option>
                  {(filters?.class_group || []).map((o: any) => (
                    <option key={o.id} value={o.id}>{o.class_group}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-subject">Subject</label>
                <select
                  id="filter-subject"
                  className="form-select"
                  value={subjectId}
                  onChange={(e) => setFilter('subject_id', e.target.value)}
                >
                  <option value="">All subjects</option>
                  {(filters?.subject || []).map((o: any) => (
                    <option key={o.id} value={o.id}>{o.subject}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-chapter">Chapter</label>
                <select
                  id="filter-chapter"
                  className="form-select"
                  value={chapterId}
                  disabled={!subjectId}
                  onChange={(e) => setFilter('chapter_id', e.target.value)}
                >
                  <option value="">{subjectId ? 'All chapters' : 'Pick a subject first'}</option>
                  {chaptersForSubject.map((o: any) => (
                    <option key={o.id} value={o.id}>{o.chapter_name}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-type">Content</label>
                <select
                  id="filter-type"
                  className="form-select"
                  value={contentTypeId}
                  onChange={(e) => setFilter('content_type_id', e.target.value)}
                >
                  <option value="">All content</option>
                  {(filters?.content_type || []).map((o: any) => (
                    <option key={o.id} value={o.id}>{o.content_type}</option>
                  ))}
                </select>
              </div>
            </div>

            {hasFilters && (
              <div className="mt-3 d-flex align-items-center gap-3 flex-wrap">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                  <i className="fas fa-times me-2"></i>Clear filters
                </button>
                <span className="text-muted small">
                  {total} {total === 1 ? 'item' : 'items'} match
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading</span>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span>{error}</span>
            <button type="button" className="btn btn-sm btn-danger" onClick={loadContent}>
              Try again
            </button>
          </div>
        )}

        {/* Filters that cannot match anything are reachable on purpose -- exam
            and subject are not narrowed against each other, so Maths under NEET
            is a legal selection with no content. Say that plainly instead of
            showing a bare empty grid. */}
        {!loading && !error && items.length === 0 && (
          <div className="text-center py-5 empty-state">
            <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
            <h5 className="fw-semibold">Nothing published here yet</h5>
            <p className="text-muted mb-3">
              {hasFilters
                ? 'No content matches this combination. Some subjects only appear under one exam.'
                : 'Content will appear here as soon as it is published.'}
            </p>
            {hasFilters && (
              <button type="button" className="btn btn-primary" onClick={clearFilters}>
                Show everything
              </button>
            )}
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="row g-4">
              {items.map((item) => (
                <div className="col-12 col-sm-6 col-lg-4" key={item.id}>
                  <button
                    type="button"
                    className={`content-card ${TYPE_CLASS[item.content_type] || ''}`}
                    onClick={() => openItem(item)}
                  >
                    <div className="content-card-top">
                      <span className="type-chip">{item.content_type}</span>
                      <i
                        className={`fas ${item.media === 'video' ? 'fa-play-circle' : 'fa-file-pdf'} content-card-icon`}
                        aria-hidden="true"
                      ></i>
                    </div>

                    <h6 className="content-card-title">{item.title}</h6>

                    {item.description && (
                      <p className="content-card-desc">{item.description}</p>
                    )}

                    <div className="content-card-meta">
                      <span>{item.subject}</span>
                      <span className="dot">·</span>
                      <span>Class {item.class_group}</span>
                      <span className="dot">·</span>
                      <span>{item.exam_name}</span>
                    </div>

                    {item.chapter_id ? (
                      <div className="content-card-chapter">
                        <i className="fas fa-bookmark me-2"></i>{item.chapter_name}
                      </div>
                    ) : null}

                    <div className="content-card-action">
                      {item.media === 'video'
                        ? 'Watch video'
                        : `Open PDF${item.file_size_bytes ? ` · ${formatFileSize(item.file_size_bytes)}` : ''}`}
                      <i className="fas fa-arrow-right ms-2"></i>
                    </div>
                  </button>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <nav className="mt-5 d-flex justify-content-center" aria-label="Content pages">
                <ul className="pagination mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                      Previous
                    </button>
                  </li>
                  <li className="page-item disabled">
                    <span className="page-link">Page {page} of {totalPages}</span>
                  </li>
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AcademicContent;
